import { ApiClient } from './apiClient';
import {
  CopyPOrderRequest,
  CopyPOrderResponse,
  GenerateRandomPOrderRequest,
  GenerateRandomPOrderResponse,
  CreatePOrderWithLinesRequest,
  CreatePOrderWithLinesResponse,
  AvailableItemForPOrder,
  Warehouse,
  ValidateOrderNoERPResponse,
  GenerateOrderNoERPResponse,
  NextPOrderNoResponse
} from '../types/porderRegistration';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class POrderRegistrationService {
  /**
   * Copy an existing purchase order
   */
  async copyPOrder(orderNo: number): Promise<CopyPOrderResponse> {
    const request: CopyPOrderRequest = { sourceOrderNo: orderNo };
    const response = await ApiClient.post(`${API_BASE_URL}/porders/${orderNo}/copy`, request);

    if (!response.ok) {
      throw new Error(`Failed to copy purchase order: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Generate a random test purchase order
   */
  async generateRandomPOrder(request: GenerateRandomPOrderRequest): Promise<GenerateRandomPOrderResponse> {
    const response = await ApiClient.post(`${API_BASE_URL}/porders/generate-random`, request);

    if (!response.ok) {
      throw new Error(`Failed to generate random purchase order: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create a purchase order with lines
   */
  async createPOrderWithLines(request: CreatePOrderWithLinesRequest): Promise<CreatePOrderWithLinesResponse> {
    const response = await ApiClient.post(`${API_BASE_URL}/porders/with-lines`, request);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to create purchase order'));
    }

    return await response.json();
  }

  /**
   * Get available items for purchase order creation
   */
  async getAvailableItems(requireStock: boolean = false): Promise<AvailableItemForPOrder[]> {
    const response = await ApiClient.get(`${API_BASE_URL}/porders/available-items?requireStock=${requireStock}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch available items: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get list of warehouses
   */
  async getWarehouses(): Promise<Warehouse[]> {
    const url = `${API_BASE_URL}/porders/warehouses`;
    console.log('Fetching warehouses from:', url);
    console.log('API_BASE_URL:', API_BASE_URL);

    const response = await ApiClient.get(url);
    console.log('Warehouses response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Failed to fetch warehouses. Status:', response.status, 'Response:', errorData);
      throw new Error(extractApiError(errorData, response, 'Failed to fetch warehouses'));
    }

    const data = await response.json();
    console.log('Warehouses data received:', data);
    return data;
  }

  /**
   * Validate OrderNoERP
   */
  async validateOrderNoERP(orderNoERP: string): Promise<ValidateOrderNoERPResponse> {
    const response = await ApiClient.get(`${API_BASE_URL}/porders/validate-orderno/${encodeURIComponent(orderNoERP)}`);

    if (!response.ok) {
      throw new Error(`Failed to validate OrderNoERP: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get next available OrderNo
   */
  async getNextOrderNo(): Promise<number> {
    const response = await ApiClient.get(`${API_BASE_URL}/porders/next-orderno`);

    if (!response.ok) {
      throw new Error(`Failed to get next OrderNo: ${response.statusText}`);
    }

    const data: NextPOrderNoResponse = await response.json();
    return data.nextOrderNo;
  }

  /**
   * Generate unique OrderNoERP
   */
  async generateOrderNoERP(): Promise<string> {
    const response = await ApiClient.get(`${API_BASE_URL}/porders/generate-orderno`);

    if (!response.ok) {
      throw new Error(`Failed to generate OrderNoERP: ${response.statusText}`);
    }

    const data: GenerateOrderNoERPResponse = await response.json();
    return data.orderNoERP;
  }

  /**
   * Format date for display
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('no-NO');
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format quantity for display
   */
  formatQuantity(quantity?: number): string {
    if (quantity === undefined || quantity === null) return '';
    return new Intl.NumberFormat('no-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(quantity);
  }
}

export const porderRegistrationService = new POrderRegistrationService();
